import {
  base_colorAction,
  base_colorActionHover,
  base_colorContentBorder,
  base_colorSurfaceSecondary,
  base_contentBackground,
  cta_buttonSecondaryBackgroundHover,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_semibold,
  font_md,
  font_sm,
  fontBold,
  fontFormLabel,
  fontH3,
  fontRegular,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export const subtitle = css`
  ${font_md};
  color: ${text_colorTextSecondary};
`

export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

export const wizardLayout = css`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${spacing(5)};
`

export const stepperCard = css`
  flex: 0 0 ${spacing(70)};
  padding: ${spacing(5)};
`

export const contentCard = css`
  flex: 1 1 auto;
  padding: ${spacing(10)};
`

// Header block spacing is margin-driven (not a single flex gap) so the
// eyebrow→heading and heading→subtitle gaps can differ: 8px eyebrow→heading,
// 20px heading→subtitle, then 32px (margin-bottom) subtitle→content.
export const stepHeader = css`
  display: flex;
  flex-direction: column;
  ${fontRegular};
  margin-bottom: ${spacing(8)};
`

export const stepEyebrow = css`
  font-family: ${font_family_weight_semibold};
  ${font_sm};
  color: ${base_colorAction};
  margin-bottom: ${spacing(2)};
`

export const stepHeading = css`
  ${fontH3};
  color: ${text_colorTextPrimary};
  margin: 0 0 ${spacing(5)} 0;
`

export const stepBody = css`
  ${fontRegular};
  color: ${text_colorTextPrimary};
  margin: 0;
`

// Heavier weight for inline emphasis (e.g. "Mastercard"/"Visa"). Semibold is the
// heaviest Rubik face shipped in the app — no extra font file required.
export const stepBodyEmphasis = css`
  font-family: ${font_family_weight_semibold};
`

export const stepActions = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: ${spacing(8)};
`

export const stepActionsRight = css`
  display: flex;
  flex-direction: row;
  gap: ${spacing(3)};
  margin-left: auto;
`

// Muted "Go Back" affordance: grey label + chevron, not the blue text-button
// treatment. The chevron inherits the button colour via currentColor.
export const backButton = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(1)};
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${font_family_weight_medium};
  ${font_md};
  color: ${text_colorTextSecondary};

  & svg path {
    fill: currentColor;
  }

  &:hover {
    color: ${text_colorTextPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
    border-radius: ${spacing(1)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// Holds the two setup-guide cards. The inter-card gap is intentionally smaller
// than the header→list gap (stepHeader margin-bottom) so the list reads as a
// tighter group, per Figma. Group→actions spacing is owned by stepActions.
export const guideCardList = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(6)};
`

export const networkGuideCard = css`
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${cornerRadius};
  padding: ${spacing(10)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(7)};
`

export const networkGuideHeader = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing(8)};
`

export const networkGuideTitle = css`
  ${fontH3};
  color: ${text_colorTextPrimary};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing(3)};
  margin: 0;
`

export const networkGuideLogo = css`
  flex: 0 0 auto;
`

export const networkGuideBody = css`
  ${fontRegular};
  color: ${text_colorTextPrimary};
  margin: 0;
`

export const guideLinkButton = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(2)};
  padding: ${spacing(2)} ${spacing(4)};
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${spacing(2)};
  background: ${base_contentBackground};
  color: ${base_colorAction};
  font-family: ${font_family_weight_medium};
  ${font_md};
  line-height: 16px;
  text-decoration: none;
  white-space: nowrap;

  & svg path {
    fill: ${base_colorAction};
  }

  &:hover {
    background: ${cta_buttonSecondaryBackgroundHover};
    color: ${base_colorActionHover};

    & svg path {
      fill: ${base_colorActionHover};
    }
  }

  &:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
  }
`

export const fieldGroup = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(2)};
  margin-bottom: ${spacing(5)};
`

export const feedNameDivider = css`
  border-top: 1px solid ${base_colorContentBorder};
  margin-bottom: ${spacing(5)};
`

export const fieldLabel = css`
  ${fontFormLabel};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing(1)};
`

export const termsBox = css`
  background: ${base_colorSurfaceSecondary};
  border: 1px solid ${base_colorContentBorder};
  border-radius: ${cornerRadius};
  padding: ${spacing(8)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  margin-bottom: ${spacing(8)};
`

export const termsBoxIntro = css`
  ${fontBold};
  color: ${text_colorTextPrimary};
`

export const termsBoxList = css`
  ${fontRegular};
  color: ${text_colorTextSecondary};
  margin: 0;
  padding-left: ${spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
`

export const termsAcceptRow = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing(3)};
  ${fontRegular};
  color: ${text_colorTextPrimary};
  cursor: pointer;
`

export const whatHappensList = css`
  ${fontRegular};
  ${font_md};
  color: ${text_colorTextSecondary};
  margin: 0;
  padding-left: ${spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
`
